// src/components/Control/robot/RobotContext.tsx
// Всё, что общее у desktop- и mobile-раскладок страницы робота: сессия Zenoh,
// телеметрия, события, управление движением и состояние интерфейса.
// Раскладки отличаются только представлением — данные и команды одни.
import React, { createContext, useContext } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { Store, useStore } from './store';
import { Telemetry, TelemetryStore } from './telemetry';
import { EventsState, EventsStore } from './events';
import { Drive, DriveState } from './drive';
import { VoiceController, VoiceState } from './voice';
import { SnapshotController, SnapshotsState } from './snapshots';
import { ZenohSample, ZenohState } from '../../../types/zenoh';
import { NotificationItem } from '../../../types';
import { loadPref, savePref } from '../../../types/features';

export type Screen = 'map' | 'camera' | 'nanny' | 'settings';
export type SettingsSection =
  | 'autonomy' | 'cameras' | 'people' | 'lidar' | 'navigation' | 'maps' | 'nanny' | 'voice' | 'bluetooth'
  | 'geometry' | 'recording' | 'snapshots' | 'laser' | 'trainer' | 'home' | 'power' | 'notifications' | 'zenoh' | 'diagnostics';
export type CamId = '0' | '1';
export type CamChoice = '0' | '1' | 'split';
export type LayoutPreset = 'map' | 'split' | 'camera';

export interface UiState {
  screen: Screen;
  prevScreen: Screen;
  section: SettingsSection | null;
  cam: CamChoice;
  layout: LayoutPreset;
  controlOpen: boolean;
  voiceOpen: boolean;
  show: Record<CamId, boolean>;  // «показывать» камеру в этом браузере
  flash: { text: string; at: number } | null;  // короткое подтверждение («Скорость 0.25 м/с»)
}

const SHOW_KEY = 'fleetcore.showCams';
function loadShow(): Record<CamId, boolean> {
  try {
    return { '0': true, '1': true, ...JSON.parse(localStorage.getItem(SHOW_KEY) || '{}') };
  } catch {
    return { '0': true, '1': true };
  }
}

export function createUiStore(): Store<UiState> {
  return new Store<UiState>({
    screen: 'map',
    prevScreen: 'map',
    section: null,
    cam: loadPref<CamChoice>('ui.cam', '0'),
    layout: loadPref<LayoutPreset>('ui.layout', 'map'),
    controlOpen: false,
    voiceOpen: false,
    show: loadShow(),
    flash: null,
  });
}

export interface ZenohInfo {
  state: ZenohState;
  messages: ZenohSample[];
}

export interface RobotApi {
  robotId: string;
  robotName: string;
  session: zenoh.Session | null;
  zenoh: {
    store: Store<ZenohInfo>;
    connect: () => void;
    disconnect: () => void;
  };
  telemetry: TelemetryStore;
  events: EventsStore;
  drive: Drive;
  voice: VoiceController;
  snapshots: SnapshotController;
  ui: Store<UiState>;
  notify: (message: string, type?: NotificationItem['type']) => void;
  stop: () => void;                         // глобальный STOP
  goBack: () => void;                       // на список роботов
}

const Ctx = createContext<RobotApi | null>(null);

export const RobotProvider: React.FC<{ value: RobotApi; children: React.ReactNode }> = ({ value, children }) => (
  <Ctx.Provider value={value}>{children}</Ctx.Provider>
);

export function useRobot(): RobotApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useRobot outside RobotProvider');
  return v;
}

export function useTelemetryState<S>(selector: (t: Telemetry) => S): S {
  return useStore(useRobot().telemetry, selector);
}

// Есть ли шасси: колёса, лидар, карта. Нет — Pi без шасси (камера, микрофон, динамик):
// без езды, STOP, карты, лидара и автономии
export function useHasChassis(): boolean {
  return useTelemetryState(t => t.chassis);
}

// Камера 2 (ESP32-S3) — на шасси; у Pi без шасси — только если она сама прислала статус
export function useHasCam2(): boolean {
  return useTelemetryState(t => t.chassis || !!t.status?.cam2);
}

export function useEventsState<S>(selector: (e: EventsState) => S): S {
  return useStore(useRobot().events, selector);
}

export function useDriveState<S>(selector: (d: DriveState) => S): S {
  return useStore(useRobot().drive.store, selector);
}

export function useVoiceState<S>(selector: (v: VoiceState) => S): S {
  return useStore(useRobot().voice.store, selector);
}

export function useSnapshotsState<S>(selector: (s: SnapshotsState) => S): S {
  return useStore(useRobot().snapshots.store, selector);
}

export function useZenohInfo<S>(selector: (z: ZenohInfo) => S): S {
  return useStore(useRobot().zenoh.store, selector);
}

export function useUi<S>(selector: (u: UiState) => S): S {
  return useStore(useRobot().ui, selector);
}

// Действия интерфейса
export function uiActions(ui: Store<UiState>) {
  return {
    go: (screen: Screen) => ui.set(s => ({ screen, prevScreen: s.screen === 'settings' ? s.prevScreen : s.screen, controlOpen: false })),
    openSettings: (section: SettingsSection | null = null) =>
      ui.set(s => ({ screen: 'settings', section, prevScreen: s.screen === 'settings' ? s.prevScreen : s.screen, controlOpen: false })),
    closeSettings: () => ui.set(s => ({ screen: s.prevScreen, section: null })),
    setCam: (cam: CamChoice) => {
      savePref('ui.cam', cam);
      ui.set({ cam });
    },
    setLayout: (layout: LayoutPreset) => {
      savePref('ui.layout', layout);
      ui.set({ layout });
    },
    setShow: (cam: CamId, value: boolean) => {
      const show = { ...ui.get().show, [cam]: value };
      try {
        localStorage.setItem(SHOW_KEY, JSON.stringify(show));
      } catch {
        /* private mode: keep it for this page only */
      }
      ui.set({ show });
    },
    toggleControl: (open?: boolean) => ui.set(s => ({ controlOpen: open ?? !s.controlOpen })),
    toggleVoice: (open?: boolean) => ui.set(s => ({ voiceOpen: open ?? !s.voiceOpen })),
    flash: (text: string) => {
      const at = Date.now();
      ui.set({ flash: { text, at } });
      setTimeout(() => ui.set(s => (s.flash?.at === at ? { flash: null } : {})), 1500);
    },
  };
}

export function useUiActions() {
  return uiActions(useRobot().ui);
}
